import config from "lib/config"
import { TRACKING_EVENTS } from "lopermut"
import mixpanel from "mixpanel-browser"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useCallback, useEffect } from "react"

const SHOULD_TRACK_EVENTS = config.isProduction

type TrackingEvent = (typeof TRACKING_EVENTS)[keyof typeof TRACKING_EVENTS]

export function setUpMixpanel() {
  if (!SHOULD_TRACK_EVENTS) return

  mixpanel.init(config.mixpanelToken, {
    debug: !config.isProduction,
    ignore_dnt: true,
    track_pageview: false,
    autocapture: false,
    persistence: "localStorage",
  })
}

export function useTrackEvent() {
  const { data } = useAuthEmployeeQuery()

  return useCallback(
    (event: TrackingEvent, properties?: Record<string, unknown>) => {
      if (!SHOULD_TRACK_EVENTS || !data) return

      mixpanel.track(event, {
        ...properties,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_title: document.title,
        isAdmin: data.isAdmin,
        backofficeType: data.backofficeType,
        adminId: data.adminId,
      })
    },
    [data],
  )
}

type TrackPageviewProps = {
  path: string
  params: object
  search: object
}

// NOTE: Mixpanel auto pageview is a bit meh and does not play well with our router, using a custom
// pageview tracking instead
export function useTrackPageview() {
  const { data } = useAuthEmployeeQuery()

  return useCallback(
    ({ path, params, search }: TrackPageviewProps) => {
      if (!SHOULD_TRACK_EVENTS || !data) return
      const cleanedPath = path.replace(/^\/|\/$/g, "")

      mixpanel.track(TRACKING_EVENTS.PAGE_VIEW, {
        page_url: window.location.href,
        page_path: cleanedPath,
        page_params: params,
        page_search: search,
        page_title: document.title,
        isAdmin: data.isAdmin,
        backofficeType: data.backofficeType,
        adminId: data.adminId,
      })
    },
    [data],
  )
}

export function useIdentify() {
  const { data } = useAuthEmployeeQuery()

  useEffect(() => {
    if (!SHOULD_TRACK_EVENTS || !data) return

    mixpanel.people.set({
      $email: data.email,
      $name: data.name,
      locale: data.locale,
      organizationId: data.organizationId,
      organizationName: data.organization.name,
      companyId: data.companyId,
      companyName: data.company?.name,
    })
    mixpanel.identify(data.id)
  }, [data])
}
